import React, { useEffect, useState, useCallback } from "react";
import { useStyles } from "./AutoSync.styles";
import { Grid, Button, FormControlLabel, Switch } from "@material-ui/core";
import clsx from "clsx";
import { FormProvider, Controller, useForm } from "react-hook-form";
import { useConfirmationDialog } from "../../../../hooks/useConfirmationDialog";
import apiClient from "../../../../auth/apiClient";
import RoutineService from "../../../../services/RoutineService";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import useCurrentFacility from "../../../../hooks/useCurrentFacility";
import { ROUTINE_TYPE } from "../../../../constants/index";
import ScheduleTable from "./ScheduleTable/ScheduleTable";
import ContractService from "../../../../services/ContractService";
import AccessGroupService from "../../../../services/AccessGroupService";
import { useSelector } from "react-redux";

const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const DAY_TO_CRON = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
};

const AutoSyncPage = () => {
    const classes = useStyles();
    const routineService = new RoutineService(apiClient);
    const contractService = new ContractService(apiClient);
    const accessGroupService = new AccessGroupService(apiClient);
    const { showDialog } = useConfirmationDialog();
    const enqueueSnackbar = useEnqueueSnackbar();
    const { facilityID } = useCurrentFacility();
    const scopeAwareFacilityID = useSelector((state) => state.entityScope?.facilityGroupId || facilityID);

    const methods = useForm({
        defaultValues: {
            isAutoSyncEnable: false,
        },
        mode: "onChange",
    });
    const { control, handleSubmit, reset } = methods;

    const [routineId, setRoutineId] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const defaultRoutine = {
        id: routineId ?? 0,
        entityId: scopeAwareFacilityID,
        name: "Access Group Auto Sync",
        cronSchedule: null,
        systemEventId: null,
        actions: [],
        isHidden: true
    };

    const [currentRoutine, setCurrentRoutine] = useState(defaultRoutine);

    const [schedule, setSchedule] = useState(
        dayNames.map((day) => ({ day, enabled: false, time: null }))
    );

    const handleScheduleChange = useCallback((updatedSchedule) => {
        setSchedule(updatedSchedule);
    }, []);

    const handleSyncNow = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const selectedStatus = "neutral";
            const response = await showDialog({
                title: "Sync Now Confirmation",
                message:
                    "Please confirm you want to sync access groups with auto sync enabled now?",
                buttons: [
                    { name: "Submit", color: "primary" },
                    { name: "Cancel", color: "secondary" },
                ],
            });
            if (response === "Cancel") return;

            try {
                const affectedGroups = await accessGroupService.GetAccessGroupIdsForAutoSync(scopeAwareFacilityID);
                const accessGroupIds = affectedGroups?.data;
                if (!Array.isArray(accessGroupIds)) {
                    toastMessage("We encountered a problem retrieving the access groups", "error", "ErrorRetrievingAccessGroups");
                    return;
                }

                if (accessGroupIds.length === 0) {
                    toastMessage("No access groups found to update", "info", "NoAccessGroupsFound");
                    return;
                }

                await contractService.syncAccessHolderStatusByGroup({
                    AccessGroupIDs: accessGroupIds,
                    Status: selectedStatus,
                    EntityID: scopeAwareFacilityID,
                });

                toastMessage(
                    `Successfully updated status for access holders to: ${selectedStatus}`,
                    "success", "UpdatedStatusOfAccessHolders"
                );
            } catch (err) {
                toastMessage(
                    `Failed to update status for access holders to: ${selectedStatus}`,
                    "error", "FailedToUpdateStatusOfAccessholders"
                );
            }
        } finally {
            setIsSaving(false);
        }
    };

    const parseAmPm = (timeStr) => {
        if (typeof timeStr !== 'string') {
            throw new Error('time must be a string in AM/PM format');
        }
        const s = timeStr.trim().toUpperCase();
        // Regex: capture hour, minute, and AM/PM with optional space before AM/PM
        const m = s.match(/^(\d{1,2}):(\d{1,2})\s*(AM|PM)$/);
        if (!m) {
            throw new Error(`Invalid time format: "${timeStr}". Expected "h:mm AM/PM"`);
        }
        let hour = parseInt(m[1], 10);
        let minute = parseInt(m[2], 10);
        const meridiem = m[3]; // "AM" or "PM"
        // Validate ranges
        if (!(hour >= 1 && hour <= 12) || !(minute >= 0 && minute <= 59)) {
            throw new Error(`Invalid time values: hour=${hour}, minute=${minute}`);
        }
        // Convert to 24-hour
        if (meridiem === 'AM') {
            hour = (hour === 12) ? 0 : hour; // 12:xx AM -> 00:xx
        } else {
            hour = (hour === 12) ? 12 : hour + 12;// PM: 12:xx PM stays 12, otherwise add 12
        }
        const hour24 = String(hour);
        const minuteStr = String(minute);

        return { hour: hour24, minute: minuteStr };
    }

    const createCronSchedules = (payload) => {
        if (!payload) return '[]';

        const cronArray = (payload?.schedule ?? [])
            .filter(p =>
                p.enabled &&
                p.time
            )
            .map(p => {
                const timeStr = (typeof p.time === 'string' && p.time.trim()) ? p.time.trim() : "00:00 AM";
                const { hour, minute } = parseAmPm(timeStr);
                const dayOfWeek = DAY_TO_CRON[p.day.toLowerCase()];

                return `${minute} ${hour} * * ${dayOfWeek}`;
            })

        return JSON.stringify(cronArray);
    }

    const buildAutoSyncAction = () => {
        const schedulePayload = schedule
            .filter(({ enabled, time }) => enabled)
            .map(({ day, enabled, time }) => ({
                day,
                enabled,
                time: time || null,
            }));

        const existing = currentRoutine?.actions?.find(
            (a) => a.actionTypeID === ROUTINE_TYPE.ACCESS_GROUP_AUTOSYNC
        );
        const order = existing?.order ?? (currentRoutine?.actions?.length || 0) + 1;
        const autoSyncEnable = methods.getValues("isAutoSyncEnable");

        return {

            payload: {
                isAutoSyncEnable: autoSyncEnable,
                schedule: schedulePayload,
            },
            actionTypeID: ROUTINE_TYPE.ACCESS_GROUP_AUTOSYNC,
            order,
        };
    };

    const upsertAction = (actions, action) => {
        const idx = actions.findIndex((a) => a.order === action.order);
        if (idx >= 0) {
            const copy = [...actions];
            copy[idx] = action;
            return copy;
        }
        return [...actions, action];
    };

    const persistRoutine = async (currentRoutine) => {
        try {
            const isAutoSyncEnable = methods.getValues("isAutoSyncEnable");
            const hasId = Number(currentRoutine?.id) > 0;

            const response = hasId
                ? await routineService.updateRoutine(currentRoutine)
                : await routineService.createRoutine(currentRoutine);

            const savedRoutine = response?.data;
            setCurrentRoutine(savedRoutine);

            if (savedRoutine?.id) {
                if (isAutoSyncEnable)
                    await routineService.enableRoutine(savedRoutine.id);
                else
                    await routineService.deleteRoutine(savedRoutine.id);
            }

            toastMessage("auto sync routine saved successfully", "success", "autoSyncRoutineSaved");
        } catch (error) {
            toastMessage("Failed to save auto sync routine", "error", "saveAutoSyncRoutineError");
        }
    };

    const toastMessage = useCallback((msg, variant, tag) => {
        enqueueSnackbar(msg, { variant: variant, tag: tag });
    }, []);

    const onSaveClick = useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            const hasMissingTime = schedule.some(row => row.enabled && !row.time);
            if (hasMissingTime) {
                toastMessage("Unable to save, please select time for all enabled days", "error", "selectTimeForEnableDays");
                return;
            }
            const action = buildAutoSyncAction();
            const nextActions = upsertAction(currentRoutine.actions ?? [], action);
            const cronSchedules = createCronSchedules(nextActions[0]?.payload || '[]');
            const nextRoutine = { ...currentRoutine, actions: nextActions, cronSchedule: cronSchedules };
            await persistRoutine(nextRoutine);
        } finally {
            setIsSaving(false);
        }
    });

    const GetRoutine = async () => {
        try {
            const result = await routineService.getAutoSyncRoutine(scopeAwareFacilityID);
            const routine = result?.data;
            const routineId = routine?.id ?? 0
            setCurrentRoutine(routineId > 0 ? routine : defaultRoutine);
            setRoutineId(routineId);
            const autoSyncAction = routine?.actions?.find(
                (a) => a.actionTypeID === ROUTINE_TYPE.ACCESS_GROUP_AUTOSYNC
            );
            const payload = autoSyncAction?.payload;
            if (payload) {
                const autosyncEnable = Boolean(payload.isAutoSyncEnable);
                const newValues = {
                    isAutoSyncEnable: autosyncEnable,
                };
                reset(newValues);
                const scheduleArray = Array.isArray(payload.schedule)
                    ? payload.schedule
                    : [];
                const normalized = dayNames.map((day) => {
                    const found = scheduleArray.find((p) => String(p.day) === day);
                    return {
                        day,
                        enabled: Boolean(found?.enabled),
                        time:
                            typeof found?.time === "string" && found?.time.trim()
                                ? found.time.trim()
                                : null,
                    };
                });

                setSchedule(normalized);
            } else {
                const newValues = {
                    isAutoSyncEnable: false,
                };
                reset(newValues);
                setSchedule(
                    dayNames.map((day) => ({
                        day,
                        enabled: false,
                        time: null,
                    }))
                );
            }
        } catch (error) {
            const status = error?.response?.status;
            if (status === 404) {
                return;
            }

            toastMessage("Failed to get auto sync routine", "error", "getRoutineError");
        }
    };

    useEffect(() => {
        if (scopeAwareFacilityID) GetRoutine();
    }, [scopeAwareFacilityID]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSaveClick)}>
                <Grid item xs={12} className={classes.headerContainer}>
                    <Grid item xs={6}>
                        <h2 className={clsx(classes.text)}>Auto Sync Schedule</h2>
                    </Grid>
                    <Grid item xs={6} className={classes.syncNowBtnGrp}>
                        <Button
                            color="primary"
                            variant="contained"
                            data-testid="syncnow-btn"
                            onClick={handleSyncNow}
                            disabled={isSaving}
                        >
                            SYNC NOW
                        </Button>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="isAutoSyncEnable"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                role="isAutoSyncEnable-toggle"
                                label="Enable Auto Sync"
                                control={
                                    <Switch
                                        {...field}
                                        id="isAutoSyncEnable"
                                        color="primary"
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            field.onChange(checked);
                                        }}
                                        checked={!!field.value}
                                        data-checked={!!field.value}
                                    />
                                }
                                className={clsx([classes.ToggleContainer])}
                            />
                        )}
                    />
                </Grid>

                <ScheduleTable initialSchedule={schedule} onScheduleChange={handleScheduleChange} />

                <div className={classes.saveBtnGrp}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        data-testid="save-btn"
                        disabled={isSaving}
                    >
                        Save
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};

export default AutoSyncPage;